<template>
  <div>
    <PageHeader :title="$t('printList.title')" />
    <section>
      <DxDataGrid
        id="printListGridContainer"
        :data-source="dataStore"
        :ref="dataGridRefName"
        :remote-operations="true"
        :showBorders="false"
        :columnAutoWidth="true"
        :allowColumnReordering="true"
        :allowColumnResizing="true"
        :rowAlternationEnabled="false"
        :showColumnLines="true"
        :showRowLines="true"
        :no-data-text="$t('common.grid.noRecordsYet')"
        @content-ready="contentReady($event)"
        @editor-preparing="onEditorPreparing"
        @data-error-occurred="onDataErrorOccurred($event)"
        :rtl-enabled="rtlEnabled"
      >
        <DxColumnChooser :enabled="true" mode="select" :title="$t('common.grid.columnChooser')"/>
        <DxFilterRow :visible="true" />
        <DxHeaderFilter :visible="true" />
        <DxScrolling mode="standard" :scroll-by-content="rtlEnabled" :use-native="!rtlEnabled" />
        <DxPaging :page-size="50" />
        <DxPager
          :visible="true"
          :allowed-page-sizes="[50, 100, 150, 200]"
          :display-mode="'full'"
          :show-page-size-selector="true"
          :show-info="true"
          :show-navigation-buttons="true"
          :infoText="rtlEnabled ?  'صفحة {0} من {1} ({2} إجمالي العناصر)' : ''"
        />
        <DxStateStoring
          :enabled="true"
          type="custom"
          :customSave="customSave"
          :customLoad="customLoad"
        />

        <DxToolbar>
          <DxItem location="before" template="columnButtonTemplate" />
          <DxItem location="before" template="exportButtonTemplate" />
          <DxItem location="before" template="clearAllTemplate" />
        </DxToolbar>

        <template #exportButtonTemplate>
          <DxDropDownButton
            class="export-dropdown"
            :items="buttonData"
            :text="$t('common.grid.export')"
            @item-click="onItemClick"
          />
        </template>

        <template #columnButtonTemplate>
          <button
            class="custom-columns"
            @click="dataGrid.showColumnChooser()"
          >
            <span class="custom-text">{{ $t('common.grid.columns') }} </span>
            <span class="custom-icon">            
              <i class="dx-icon dx-icon-spindown dx-icon-right"></i>
            </span>
          </button>
        </template>

        <template #clearAllTemplate>
          <div v-if="showClearAll" class="clear-cols d-flex align-items-center">
            <div class="toolbar-custom-line"></div>
            <Button
              :text="$t('common.grid.clearFilters')"
              @click="reset()"
              styling-mode="outlined"
              type="danger"
              class="clear-CustomColums"
            />
          </div>
        </template>

        <DxColumn
          :caption="$t('common.userName')"
          data-field="userName"
          :min-width="200"
          
          :allow-editing="false"
          :allow-filtering="true"
          :allow-header-filtering="false"
        />

        <DxColumn
          :caption="$t('printList.printer')"
          data-field="device_name"
          :min-width="200"
         
          :allow-editing="false"
          :allow-filtering="false"
          :allow-header-filtering="true"
        />

        <DxColumn
          :caption="$t('common.type')"
          data-field="print_data.type"
          :min-width="200"
         
          :allow-editing="false"
          :allow-filtering="false"
          :allow-header-filtering="true"
          :calculate-display-value="calculatePrintType"
        >
          <DxHeaderFilter :data-source="printTypeDataSource" />
        </DxColumn>

        <DxColumn
          :caption="$t('printList.printStatus')"
          data-field="print_status"
          :calculate-display-value="calculatePrinterStatus"
          alignment="start"
          :allow-editing="false"
          :allow-filtering="false"
          :allow-header-filtering="true"
          :min-width="200"
        >
          <DxHeaderFilter :data-source="printStatusHeaderDataSource" />
        </DxColumn>

        <DxColumn
          :caption="$t('printList.expiredDate')"
          data-field="expired_at"
          :min-width="200"
          
          :allow-editing="false"
          :allow-filtering="false"
          :allow-header-filtering="true"
          :calculate-display-value="calculateExpiredTime"
        >
          <DxHeaderFilter :data-source="expiredDateHeaderDataSource" />
        </DxColumn>

        <DxColumn
          :caption="$t('printList.executedDate')"
          data-field="executed_at"
          :min-width="200"
          
          :allow-editing="false"
          :allow-filtering="false"
          :allow-header-filtering="true"
          :calculate-display-value="calculateExectuedTime"
        >
          <DxHeaderFilter :data-source="executedDateHeaderDataSource" />
        </DxColumn>

        <DxColumn
          :caption="$t('common.grid.actions')"
          data-field="updated_at"
          :min-width="200"
          :min-height="160"
          
          :allow-editing="false"
          :allow-filtering="false"
          :allow-header-filtering="false"
          cell-template="actions-cell"
        />
        <template #actions-cell="{ data }">
          <template
            v-if="data.data.print_status == 2 || data.data.print_status == 3"
          >
            <p @click="reprint(data.data.id)" class="action-cell">{{ $t('printList.reprint') }}</p>
          </template>
          <template v-if="data.data.print_status === 0">
            <p @click="cancelPrint(data.data.id)" class="action-cell">{{ $t('printList.cancelPrint') }}</p> 
          </template>
          <template v-else>
            -
          </template>
        </template>
      </DxDataGrid>
    </section>
  </div>
</template>

<script>
const dataGridRefName = "printerQueue";
const PRINT_TYPE = {
  local: "local_qr",
  passport: "passport_qr",
};

import gridServices from "@/services/reports";
import { exportDataGrid } from "devextreme/excel_exporter";
import { exportDataGrid as exportDataGridToPdf } from "devextreme/pdf_exporter";
import { default as Button } from "devextreme-vue/button";
import { Workbook } from "exceljs";
import saveAs from "file-saver";
import { jsPDF } from "jspdf";
import { mapGetters, mapActions } from "vuex";
import moment from "moment";
import PageHeader from "@/components/page-header";
import DxDropDownButton from "devextreme-vue/drop-down-button";
import amiriFont from "@/assets/fonts/Amiri-Regular.ttf"; // Load the Cairo font

import DxDataGrid, {
  DxColumn,
  DxToolbar,
  DxItem,
  DxColumnChooser,
  DxFilterRow,
  DxHeaderFilter,
  DxStateStoring,
  DxPager,
  DxPaging,
  DxScrolling,
} from "devextreme-vue/data-grid";

export default {
  title: "Print List",
  components: {
    DxDataGrid,
    DxColumn,
    DxToolbar,
    DxItem,
    DxDropDownButton,
    DxColumnChooser,
    Button,
    DxFilterRow,
    DxHeaderFilter,
    DxScrolling,
    DxPager,
    DxPaging,
    DxStateStoring,
    PageHeader
  },
  data() {
    return {
      dataGridRefName,
      printTypeDataSource: [],
      printStatusHeaderDataSource: [],
      expiredDateHeaderDataSource: [],
      executedDateHeaderDataSource: [],
      userTimezone: moment.tz.guess(),
      buttonData: ["Excel", "CSV", "PDF"],
      showClearAll: false,
      dataStore: [],
      rtlEnabled : false
      
    };
  },
  async beforeMount() {
    if (this.getLanguage == 'ar') {
      this.rtlEnabled = true;
    }
    const res = await gridServices.getPrintsData();
    this.dataStore = res.data;
  },
  methods: {
    ...mapActions("grid", [
      "onTableHeader",
      "updateTableHeaders",
      "createTableHeaders",
    ]),
    async cancelPrint(print_queue_id) {
      await gridServices.cancelRfidPrint(print_queue_id);
      await this.dataGrid.refresh();
    },
    async reprint(print_queue_id) {
      await gridServices.reprintRfid(print_queue_id);
      await this.dataGrid.refresh();
    },
    elementExists(sourceArry, elem) {
      return sourceArry.find((e) => e?.text == elem);
    },
    calculatePrinterStatus({ print_status }) {
      let status = "";
      let isPrintStatus;
      let isExpiredStatus;
      let isFitStatus;
      switch (print_status) {
        case 1:
          status = "Print";

          isPrintStatus = this.elementExists(
            this.printStatusHeaderDataSource,
            "Print"
          );

          if (!isPrintStatus)
            this.printStatusHeaderDataSource.push({
              text: "Print",
              value: ["print_status", "contains", ["1"]],
            });
          break;
        case 2:
          status = "Expired";

          isExpiredStatus = this.elementExists(
            this.printStatusHeaderDataSource,
            "Expired"
          );

          if (!isExpiredStatus)
            this.printStatusHeaderDataSource.push({
              text: "Expired",
              value: ["print_status", "contains", ["2"]],
            });
          break;
        case 3:
          status = "Fit";

          isFitStatus = this.elementExists(
            this.printStatusHeaderDataSource,
            "Fit"
          );

          if (!isFitStatus)
            this.printStatusHeaderDataSource.push({
              text: "Fit",
              value: ["print_status", "contains", ["3"]],
            });
          break;
        default:
          status = "-";
          break;
      }
      return status;
    },
    calculatePrintType({ print_data }) {
      let type = "";
      let isLocalQr;
      let isPassportQr;
      switch (print_data?.type) {
        case PRINT_TYPE.local:
          type = "Local QR";
          isLocalQr = this.elementExists(this.printTypeDataSource, "Local QR");
          if (!isLocalQr)
            this.printTypeDataSource.push({
              text: "Local QR",
              value: ["print_data.type", "contains", ["local_qr"]],
            });
          break;
        case PRINT_TYPE.passport:
          type = "Passport QR";
          isPassportQr = this.elementExists(
            this.printTypeDataSource,
            "Passport QR"
          );
          if (!isPassportQr)
            this.printTypeDataSource.push({
              text: "Passport QR",
              value: ["print_data.type", "contains", ["passport_qr"]],
            });
          break;
        default:
          type = "-";
          break;
      }
      return type;
    },
    calculateExpiredTime({ expired_at }) {
      let value;
      let isExpiredAtExists;
      if (moment(expired_at).isValid()) {
        value = moment
          .utc(expired_at)
          .tz(this.userTimezone)
          .format("DD/MM/YYYY - hh:mm:ss A");
        isExpiredAtExists = this.elementExists(
          this.expiredDateHeaderDataSource,
          value
        );
        if (!isExpiredAtExists)
          this.expiredDateHeaderDataSource.push({
            text: value,
            value: ["expired_at", "contains", [expired_at]],
          });
      }
      return value || "-";
    },
    calculateExectuedTime({ executed_at }) {
      let value;
      let isexevutedAtExists;
      if (moment(executed_at).isValid()) {
        value = moment
          .utc(executed_at)
          .tz(this.userTimezone)
          .format("DD/MM/YYYY - hh:mm:ss A");
        isexevutedAtExists = this.elementExists(
          this.executedDateHeaderDataSource,
          value
        );
        if (!isexevutedAtExists)
          this.executedDateHeaderDataSource.push({
            text: value,
            value: ["executed_at", "contains", [executed_at]],
          });
      }
      return value || "-";
    },
    onItemClick(e) {
      e.rtlEnabled = this.rtlEnabled;
      if (e.itemData == "Excel") {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("Main sheet");
        exportDataGrid({
          component: this.dataGrid,
          worksheet: worksheet,
          customizeCell: function(options) {
            const excelCell = options;
            excelCell.font = { name: "Arial", size: 12 };
            excelCell.alignment = { horizontal: "left" };
          },
        }).then(function() {
          workbook.xlsx.writeBuffer().then(function(buffer) {
            saveAs(
              new Blob([buffer], { type: "application/octet-stream" }),
              "Print List.xlsx"
            );
          });
        });
        // e.cancel = true;
      } else if (e.itemData == "CSV") {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("Main sheet");

        exportDataGrid({
          component: this.dataGrid,
          worksheet: worksheet,
          customizeCell: function(options) {
            const excelCell = options;
            excelCell.font = { name: "Arial", size: 12 };
            excelCell.alignment = { horizontal: "left" };
          },
        }).then(function() {
          workbook.csv.writeBuffer().then(function(buffer) {
            saveAs(
              new Blob([buffer], { type: "application/octet-stream" }),
              "Print List.csv"
            );
          });
        });
      } else if (e.itemData == "PDF") {
        const doc = new jsPDF('landscape');
        
        // Add custom font
        doc.addFont(amiriFont, "Amiri", "normal");
        doc.setFont("Amiri");

        doc.autoTable({ direction: "rtl" });
  
        exportDataGridToPdf({
          jsPDFDocument: doc,
          component: this.dataGrid,
          customizeCell: function (options) {
            const { gridCell, pdfCell } = options;
  
            if (gridCell.rowType === "data") {
              // data columns style
              pdfCell.styles = {
                font: e.rtlEnabled ? "Amiri" : "Lato",
                fontStyle: "normal",
                fontSize: 8,
                cellWidth: "auto",
                halign: "center",
              };
            } else {
              // header columns style
              pdfCell.styles = {
                font: e.rtlEnabled ? "Amiri" : "Lato",
                fontStyle: "normal",
                fontSize: 8,
                fillColor: "#2680EB",
                textColor: "#FFFFFF",
                cellWidth: "auto",
                halign: "center", 
              };
            }
          },
          customizeTable: (doc) => {
            doc.autoTable({ direction: "rtl" }); // Ensure table follows RTL direction
          },
        }).then(() => {
          const pageCount = doc.internal.getNumberOfPages();
          for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            const pageSize = doc.internal.pageSize;
            const pageWidth = pageSize.width
              ? pageSize.width
              : pageSize.getWidth();
            const pageHeight = pageSize.height
              ? pageSize.height
              : pageSize.getHeight();
            const header = this.$t('printList.title');
            const headerWidth = doc.getTextDimensions(header).w;
            const footer = `Page ${i} of ${pageCount}`;
  
            // Header
            doc.text(header, (pageWidth - headerWidth) / 2, 10);
  
            // Footer
            doc.text(
              footer,
              pageWidth / 2 - doc.getTextWidth(footer) / 2,
              pageHeight - 5,
              { baseline: "bottom" }
            );
          }
          doc.save("Print List.pdf");
        });
      }
    },
    contentReady(e) {
      let sortedExp = this.getSortedColumns();
      let filterExp = e.component.getCombinedFilter();
      if (filterExp || sortedExp) {
        this.showClearAll = true;
      } else {
        this.showClearAll = false;
      }
      this.setColumnChooserPosition();
    },
    onEditorPreparing(e) {
      if (e.parentType == "filterRow") {
        e.editorOptions.placeholder = this.$t("common.grid.search");
      }
    },
    getSortedColumns() {
      let allColumns = Array.from(
        Array(this.dataGrid.columnCount()).keys()
      ).map((index) => this.dataGrid.columnOption(index));
      allColumns = allColumns.find((col) => col.sortIndex != null);
      return allColumns;
    },
    setColumnChooserPosition() {
      var columnChooserView = this.dataGrid.getView("columnChooserView");
      if (!columnChooserView._popupContainer) {
        columnChooserView._initializePopupContainer();
        columnChooserView.render();
      }
      columnChooserView._popupContainer.option("position", {
        of: this.dataGrid.element(),
        my: this.rtlEnabled ? "right top" : "left top",
        at: this.rtlEnabled ? "right top" : "left top",
        offset: "0 50",
      });
    },
    reset() {
      this.dataGrid.clearFilter("row");
      this.dataGrid.clearFilter("header");
      this.dataGrid.clearSorting();     
      this.showClearAll = false;
    },
    async customLoad() {
      if (this.userEmail !== "lt_techteam@visipoint.com") {
        await this.onTableHeader({
          url: "/print-status",
        });
        let value = this.getTableHeaders;
        return value;
      }
    },
    customSave(gridState) {
      if (this.userEmail !== "lt_techteam@visipoint.com") {
        gridState.pageIndex = 0;
        gridState.pageSize = 50;
        let value = JSON.stringify(gridState);
        value = value.replace(',"selectedRowKeys":[]', "");

        if (!this.getTableHeaders) {
          this.createTableHeaders({
            url: "/print-status",
            headers: value,
            filters: "[]",
          });
        } else if (value != JSON.stringify(this.getTableHeaders)) {
          this.updateTableHeaders({
            url: "/print-status",
            headers: value,
            filters: "[]",
          });
        }
      }
    },
    onDataErrorOccurred(e){
      console.log('onDataErrorOccurred e', e);
      setTimeout(() => {  
        let errorRow = document.querySelector(".dx-datagrid-headers .dx-error-message");  
        errorRow.innerHTML = this.$t('common.notes.anErrorOccurredGrid');  
      },100);
    },
  },
  computed: {
    ...mapGetters("i18N",["getLanguage"]),
    ...mapGetters("grid", ["getTableHeaders"]),
    ...mapGetters("authfack", ["userDetails"]),

    dataGrid() {
      return this.$refs[this.dataGridRefName].instance;
    },
    userEmail() {
      return this.userDetails && this.userDetails.email;
    },
  },
};
</script>

<style scoped>
.action-cell {
  color: #103f5e;
  font-weight: 500;
  cursor: pointer;
  font-size: 14px;
  margin: 0;
}
</style>

<style lang="scss">
#printListGridContainer .dx-column-indicators {
  float: inline-end !important;
}
</style>